import { TextButton } from "@anna-money/anna-web-ui";
import type { FC } from "react";
import { useServices } from "../../../app/appServices";
import { FacebookIcon, InstagramIcon, LinkedinIcon } from "../../../icons/icons";
import type { Pp2ViewModel } from "../pp2ViewModel";
import { Pp2Icon } from "../shared/pp2Icon";
import type { Pp2CopyFeedback } from "../shared/useCopyFeedback";
import type { SocialNetwork } from "./sideBlocks";
import { mapEmbedUrl, mapTitle, socialLinks } from "./sideBlocks";
import styles from "./pp2SideBlocks.module.scss";

/**
 * The side blocks, in order About → Location → Opening hours → Social media (spec 3.4, S1–S4).
 * Only the blocks whose view-model field is non-null render, as direct children (a fragment):
 * the shell owns the side area (`data-test-id="pp2-side"`, its grid, columns and dashed border)
 * and renders it only when `vm.sideBlockCount > 0`. Each block is one grid cell.
 *
 * - `copy`: the page's single copied state; key "address" with `vm.location.address`.
 */
export type Pp2SideBlocksProps = {
  vm: Pp2ViewModel;
  copy: Pp2CopyFeedback;
};

type Location = NonNullable<Pp2ViewModel["location"]>;
type Hours = NonNullable<Pp2ViewModel["hours"]>;
type Social = NonNullable<Pp2ViewModel["social"]>;

/** Social glyphs: the library has no social icons, so the local ones are used (spec 2.4). */
const SOCIAL_ICONS: Record<SocialNetwork, FC<{ width?: string; height?: string; color?: string }>> = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  linkedin: LinkedinIcon,
};
const SOCIAL_GLYPH_PX = "27";

const AboutBlock: FC<{ text: string }> = ({ text }) => (
  <div className={styles.about} data-test-id="pp2-side-about">
    <h2 className={styles.title}>About</h2>
    <p className={styles.aboutText}>{text}</p>
  </div>
);

/** Google Maps Embed iframe, rendered directly without the Maps JS API loader (Q34). */
const Pp2Map: FC<{ placeId: string; title: string }> = ({ placeId, title }) => {
  const { config } = useServices();
  return (
    <iframe
      className={styles.map}
      title={title}
      src={mapEmbedUrl(config.googleMapsApiKey ?? "", placeId)}
      referrerPolicy="no-referrer-when-downgrade"
      loading="lazy"
      allowFullScreen
    />
  );
};

const LocationBlock: FC<{ location: Location; displayName: string; copy: Pp2CopyFeedback }> = ({
  location,
  displayName,
  copy,
}) => {
  const { address, placeId, mapsUrl } = location;
  const copied = copy.copiedKey === "address";
  return (
    <div className={styles.location} data-test-id="pp2-side-location">
      {placeId && <Pp2Map placeId={placeId} title={mapTitle(address, displayName)} />}
      {address && (
        <div className={styles.address}>
          <Pp2Icon type="actionLocation" className={styles.addressIcon} />
          <span className={styles.addressText}>{address}</span>
        </div>
      )}
      <div className={styles.actions}>
        <TextButton text="Open in Maps" size="m" href={mapsUrl} target="_blank" rel="noopener noreferrer" />
        {address && (
          <TextButton
            text={copied ? "Copied" : "Copy address"}
            size="m"
            onClick={() => {
              copy.copy("address", address);
            }}
          />
        )}
      </div>
      {address && (
        <span className={styles.srOnly} role="status">
          {copied ? "Address copied" : ""}
        </span>
      )}
    </div>
  );
};

const HoursBlock: FC<{ hours: Hours }> = ({ hours }) => (
  <div className={styles.hours} data-test-id="pp2-side-hours">
    <h2 className={styles.hoursTitle}>Opening hours</h2>
    {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver drops list semantics with list-style: none */}
    <ul className={styles.hoursRows} role="list">
      {hours.rows.map((row) => (
        <li
          key={row.days}
          className={row.isToday ? styles.todayRow : styles.hoursRow}
          data-test-id="pp2-hours-row"
          data-today={row.isToday ? "true" : undefined}
        >
          <span data-part="days">{row.days}</span>
          <span className={styles.time} data-part="time">
            {row.time}
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const SocialBlock: FC<{ social: Social }> = ({ social }) => (
  <div className={styles.social} data-test-id="pp2-side-social">
    <h2 className={styles.title}>Social media</h2>
    {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Safari/VoiceOver drops list semantics with list-style: none */}
    <ul className={styles.socialRow} role="list" aria-label="Social media">
      {socialLinks(social).map(({ network, label, href }) => {
        const Glyph = SOCIAL_ICONS[network];
        return (
          <li key={network} className={styles.socialItem}>
            <a className={styles.socialLink} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
              <span aria-hidden="true" className={styles.socialGlyph}>
                <Glyph width={SOCIAL_GLYPH_PX} height={SOCIAL_GLYPH_PX} color="currentColor" />
              </span>
            </a>
          </li>
        );
      })}
    </ul>
  </div>
);

export const Pp2SideBlocks: FC<Pp2SideBlocksProps> = ({ vm, copy }) => (
  <>
    {vm.about !== null && <AboutBlock text={vm.about} />}
    {vm.location !== null && <LocationBlock location={vm.location} displayName={vm.displayName} copy={copy} />}
    {vm.hours !== null && <HoursBlock hours={vm.hours} />}
    {vm.social !== null && <SocialBlock social={vm.social} />}
  </>
);
